// src/components/dashboard/Payments.js

import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  TablePagination,
} from "@mui/material";

// Core safe MUI Icons
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ReceiptIcon from "@mui/icons-material/Receipt";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

const Payments = () => {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const checkTheme = () => {
      const darkActive =
        document.documentElement.classList.contains("dark-theme") ||
        document.body.classList.contains("dark-theme");
      setIsDark(darkActive);
    };

    checkTheme();
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });

    return () => observer.disconnect();
  }, []);

  const [activeTab, setActiveTab] = useState(0); // 0 = New, 1 = Posted
  const [isSyncing, setIsSyncing] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Unified Clinical Design System Palette
  const tealMain = "#1E5E5F";
  const tealButton = "#245E65";
  const addGreen = "#057A44";
  const labelTeal = "#276A6C";
  const pastelTealBg = "#D9EBEF";

  const cardBg = isDark ? "#0F172A" : "#FFFFFF";
  const cardBorder = isDark ? "rgba(255, 255, 255, 0.08)" : "#245E65";
  const softCardBorder = isDark ? "rgba(255, 255, 255, 0.08)" : "#EAEFF4";
  const primaryTextColor = isDark ? "#F8FAFC" : "#0A2540";
  const secondaryTextColor = isDark ? "#94A3B8" : "#64748B";
  const controlBg = isDark ? "rgba(255, 255, 255, 0.03)" : "#FFFFFF";
  const rowHoverBg = isDark ? "rgba(30, 94, 95, 0.08)" : "rgba(36, 94, 101, 0.04)";
  const tableHeaderBg = isDark ? "#162238" : "#F4F9F9";

  // New ERA Payments Received (Unposted)
  const [newPayments, setNewPayments] = useState([
    {
      id: "ERA-9401",
      payerName: "Blue Cross Blue Shield",
      paidAmount: "$1,450.00",
      checkDate: "09/10/2026",
      checkNumber: "CHK-8891024",
      checkAmount: "$1,450.00",
      status: "Unposted",
    },
    {
      id: "ERA-9402",
      payerName: "UnitedHealthcare Choice",
      paidAmount: "$2,180.00",
      checkDate: "09/09/2026",
      checkNumber: "EFT-7710294",
      checkAmount: "$2,180.00",
      status: "Unposted",
    },
    {
      id: "ERA-9403",
      payerName: "Aetna Global Health",
      paidAmount: "$890.00",
      checkDate: "09/08/2026",
      checkNumber: "CHK-5501923",
      checkAmount: "$890.00",
      status: "Unposted",
    },
    {
      id: "ERA-9404",
      payerName: "Cigna Commercial Care",
      paidAmount: "$640.00",
      checkDate: "09/07/2026",
      checkNumber: "EFT-4410928",
      checkAmount: "$640.00",
      status: "Unposted",
    },
  ]);

  // Posted Historical Payments
  const [postedPayments, setPostedPayments] = useState([
    {
      id: "PST-8101",
      payerName: "Humana Medicare Advantage",
      paidAmount: "$3,250.00",
      checkDate: "09/01/2026",
      checkNumber: "EFT-9910231",
      checkAmount: "$3,250.00",
      status: "Posted",
    },
    {
      id: "PST-8102",
      payerName: "Good Health Insurance Corp",
      paidAmount: "$1,120.00",
      checkDate: "08/28/2026",
      checkNumber: "CHK-6629104",
      checkAmount: "$1,120.00",
      status: "Posted",
    },
  ]);

  const handleSync = () => {
    setIsSyncing(true);
    setTimeout(() => {
      setIsSyncing(false);
    }, 700);
  };

  const handlePostPayment = (id) => {
    const itemToPost = newPayments.find((p) => p.id === id);
    if (itemToPost) {
      setNewPayments((prev) => prev.filter((p) => p.id !== id));
      setPostedPayments((prev) => [
        { ...itemToPost, id: `PST-${id.split("-")[1]}`, status: "Posted" },
        ...prev,
      ]);
    }
  };

  const currentDataset = activeTab === 0 ? newPayments : postedPayments;

  const totalNewAmount = newPayments.reduce(
    (acc, curr) => acc + parseFloat(curr.paidAmount.replace("$", "").replace(",", "")),
    0
  );

  return (
    <Box sx={{ width: "100%", pb: 6 }}>
      {/* 1. TOP HEADER & SYNC CONTROLLER */}
      <Box
        sx={{
          backgroundColor: cardBg,
          borderRadius: "20px",
          border: `1.5px solid ${cardBorder}`,
          p: { xs: 2.5, md: "22px 28px" },
          boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.25)" : "0 2px 10px rgba(10, 37, 64, 0.02)",
          mb: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            justifyContent: "space-between",
            gap: 2,
            mb: 2.8,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "12px",
                backgroundColor: tealMain,
                color: "#FFFFFF",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <AccountBalanceWalletIcon sx={{ fontSize: "1.7rem" }} />
            </Box>

            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                <Typography
                  sx={{
                    fontSize: { xs: "1.2rem", md: "1.35rem" },
                    fontWeight: 800,
                    color: primaryTextColor,
                    letterSpacing: "-0.3px",
                    lineHeight: 1.2,
                  }}
                >
                  Payment Management
                </Typography>
                <Chip
                  label="ERA 835 Live"
                  size="small"
                  sx={{
                    height: "22px",
                    fontSize: "0.68rem",
                    fontWeight: 700,
                    backgroundColor: isDark ? "rgba(16, 185, 129, 0.15)" : "#DCFCE7",
                    color: isDark ? "#34D399" : "#15803D",
                    borderRadius: "6px",
                  }}
                />
              </Box>
              <Typography
                sx={{
                  fontSize: "0.82rem",
                  color: secondaryTextColor,
                  fontWeight: 500,
                  mt: 0.3,
                }}
              >
                Manage electronic remittances, claim adjustments, and automated ledger postings
              </Typography>
            </Box>
          </Box>

          <Button
            variant="contained"
            onClick={handleSync}
            disabled={isSyncing}
            startIcon={
              <RefreshIcon
                sx={{
                  fontSize: "1.15rem !important",
                  transform: isSyncing ? "rotate(360deg)" : "none",
                  transition: "transform 0.6s ease-in-out",
                }}
              />
            }
            sx={{
              height: "40px",
              backgroundColor: tealButton,
              color: "#FFFFFF",
              fontWeight: 700,
              fontSize: "0.82rem",
              borderRadius: "6px",
              px: 2.5,
              textTransform: "none",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#1c4a50",
                boxShadow: "none",
              },
            }}
          >
            {isSyncing ? "Connecting Payer..." : "Sync Remittances"}
          </Button>
        </Box>

        {/* Clinical KPI Ribbon */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
            gap: 2,
            mb: 3,
          }}
        >
          <Box
            sx={{
              p: 2,
              borderRadius: "12px",
              backgroundColor: isDark ? "#162238" : "#F8FAFC",
              border: `1px solid ${softCardBorder}`,
            }}
          >
            <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: secondaryTextColor, textTransform: "uppercase" }}>
              Pending Remittance Value
            </Typography>
            <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, color: tealButton, mt: 0.2 }}>
              ${totalNewAmount.toLocaleString("en-US", { minimumFractionDigits: 2 })}
            </Typography>
          </Box>

          <Box
            sx={{
              p: 2,
              borderRadius: "12px",
              backgroundColor: isDark ? "#162238" : "#F8FAFC",
              border: `1px solid ${softCardBorder}`,
            }}
          >
            <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: secondaryTextColor, textTransform: "uppercase" }}>
              Ready To Post
            </Typography>
            <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, color: "#057A44", mt: 0.2 }}>
              {newPayments.length} Batches
            </Typography>
          </Box>

          <Box
            sx={{
              p: 2,
              borderRadius: "12px",
              backgroundColor: isDark ? "#162238" : "#F8FAFC",
              border: `1px solid ${softCardBorder}`,
            }}
          >
            <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: secondaryTextColor, textTransform: "uppercase" }}>
              Archived / Cleared
            </Typography>
            <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, color: primaryTextColor, mt: 0.2 }}>
              {postedPayments.length} Settled
            </Typography>
          </Box>
        </Box>

        {/* Tab Selection Bar with Embedded Counts */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: `1px solid ${softCardBorder}`,
            mb: 2.5,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(_e, val) => {
              setActiveTab(val);
              setPage(0);
            }}
            sx={{
              minHeight: "42px",
              "& .MuiTabs-indicator": {
                backgroundColor: tealMain,
                height: "3px",
                borderRadius: "3px 3px 0 0",
              },
              "& .MuiTab-root": {
                textTransform: "none",
                fontSize: "0.88rem",
                fontWeight: 700,
                minWidth: "90px",
                px: 2,
                py: 0.8,
                color: secondaryTextColor,
                "&.Mui-selected": {
                  color: tealButton,
                },
              },
            }}
          >
            <Tab
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <span>New</span>
                  <Chip
                    label={newPayments.length}
                    size="small"
                    sx={{
                      height: "20px",
                      fontSize: "0.68rem",
                      fontWeight: 800,
                      backgroundColor: activeTab === 0 ? pastelTealBg : (isDark ? "rgba(255,255,255,0.08)" : "#F1F5F9"),
                      color: activeTab === 0 ? tealButton : secondaryTextColor,
                      borderRadius: "6px",
                    }}
                  />
                </Box>
              }
            />
            <Tab
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <span>Posted</span>
                  <Chip
                    label={postedPayments.length}
                    size="small"
                    sx={{
                      height: "20px",
                      fontSize: "0.68rem",
                      fontWeight: 800,
                      backgroundColor: activeTab === 1 ? pastelTealBg : (isDark ? "rgba(255,255,255,0.08)" : "#F1F5F9"),
                      color: activeTab === 1 ? tealButton : secondaryTextColor,
                      borderRadius: "6px",
                    }}
                  />
                </Box>
              }
            />
          </Tabs>

          <Typography sx={{ fontSize: "0.76rem", color: secondaryTextColor, fontWeight: 600 }}>
            Automated 835 Reconciliation Engine
          </Typography>
        </Box>

        {/* Data Table Grid */}
        <TableContainer
          sx={{
            borderRadius: "14px",
            border: `1px solid ${softCardBorder}`,
            maxHeight: "560px",
            overflowY: "auto",
            overflowX: "auto",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          <Table stickyHeader sx={{ minWidth: 920 }}>
            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    color: tealButton,
                    fontWeight: 700,
                    fontSize: "0.76rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.3px",
                    backgroundColor: tableHeaderBg,
                    borderBottom: `1px solid ${softCardBorder}`,
                    py: 1.5,
                  },
                }}
              >
                <TableCell>ID</TableCell>
                <TableCell>Payer Name</TableCell>
                <TableCell>Paid Amount</TableCell>
                <TableCell>Check Date</TableCell>
                <TableCell>Check Number</TableCell>
                <TableCell>Check Amount</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {currentDataset.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ py: 8, textAlign: "center" }}>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                      <ReceiptIcon sx={{ fontSize: "2.2rem", color: secondaryTextColor, opacity: 0.4 }} />
                      <Typography sx={{ color: secondaryTextColor, fontSize: "0.88rem", fontWeight: 600 }}>
                        No rows available in this view
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                currentDataset
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => (
                    <TableRow
                      key={row.id}
                      sx={{
                        transition: "background-color 0.15s ease",
                        "&:hover": { backgroundColor: rowHoverBg },
                        "& td": {
                          borderBottom: `1px solid ${
                            isDark ? "rgba(255, 255, 255, 0.04)" : "#F1F5F9"
                          }`,
                          py: 1.4,
                        },
                      }}
                    >
                      {/* ID Badge */}
                      <TableCell>
                        <Box
                          sx={{
                            display: "inline-block",
                            px: 1,
                            py: 0.25,
                            borderRadius: "6px",
                            backgroundColor: pastelTealBg,
                            color: tealButton,
                            fontWeight: 700,
                            fontSize: "0.75rem",
                            fontFamily: "monospace",
                          }}
                        >
                          {row.id}
                        </Box>
                      </TableCell>

                      {/* Payer Name */}
                      <TableCell sx={{ fontWeight: 700, color: primaryTextColor, fontSize: "0.84rem" }}>
                        {row.payerName}
                      </TableCell>

                      {/* Paid Amount */}
                      <TableCell>
                        <Typography sx={{ fontWeight: 800, color: "#057A44", fontSize: "0.86rem" }}>
                          {row.paidAmount}
                        </Typography>
                      </TableCell>

                      {/* Check Date */}
                      <TableCell sx={{ color: secondaryTextColor, fontSize: "0.82rem", fontWeight: 500 }}>
                        {row.checkDate}
                      </TableCell>

                      {/* Check Number */}
                      <TableCell>
                        <Chip
                          label={row.checkNumber}
                          size="small"
                          sx={{
                            height: "22px",
                            fontFamily: "monospace",
                            fontSize: "0.72rem",
                            fontWeight: 600,
                            borderRadius: "6px",
                            backgroundColor: isDark ? "rgba(255, 255, 255, 0.05)" : "#F1F5F9",
                            color: primaryTextColor,
                          }}
                        />
                      </TableCell>

                      {/* Check Amount */}
                      <TableCell sx={{ fontWeight: 700, color: primaryTextColor, fontSize: "0.84rem" }}>
                        {row.checkAmount}
                      </TableCell>

                      {/* Action */}
                      <TableCell align="right">
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.8 }}>
                          {activeTab === 0 ? (
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => handlePostPayment(row.id)}
                              endIcon={<ArrowForwardIcon sx={{ fontSize: "0.85rem !important" }} />}
                              sx={{
                                fontSize: "0.72rem",
                                fontWeight: 700,
                                borderRadius: "6px",
                                backgroundColor: tealButton,
                                color: "#FFFFFF",
                                textTransform: "none",
                                py: 0.35,
                                px: 1.4,
                                boxShadow: "none",
                                "&:hover": {
                                  backgroundColor: "#1c4a50",
                                  boxShadow: "none",
                                },
                              }}
                            >
                              Post
                            </Button>
                          ) : (
                            <Chip
                              icon={<CheckCircleOutlineIcon sx={{ fontSize: "0.85rem !important", color: "#15803D !important" }} />}
                              label="Posted"
                              size="small"
                              sx={{
                                height: "22px",
                                fontSize: "0.68rem",
                                fontWeight: 700,
                                backgroundColor: isDark ? "rgba(16, 185, 129, 0.15)" : "#DCFCE7",
                                color: isDark ? "#34D399" : "#15803D",
                                borderRadius: "6px",
                              }}
                            />
                          )}

                          <Tooltip title="View Remittance Advice">
                            <IconButton
                              size="small"
                              sx={{
                                color: secondaryTextColor,
                                "&:hover": { color: tealMain },
                              }}
                            >
                              <VisibilityIcon sx={{ fontSize: "1rem" }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Bottom Pagination Bar */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          component="div"
          count={currentDataset.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          sx={{
            borderTop: `1px solid ${softCardBorder}`,
            color: secondaryTextColor,
            "& .MuiTablePagination-select": {
              color: primaryTextColor,
              fontWeight: 600,
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default Payments;